import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface VariantRowState {
  expanded: boolean;
  hasOverflow: boolean;
  scrollLeft: number;
  /** The row has been scrolled away from its start at least once. */
  hasLeftStart: boolean;
  inLineStart: boolean;
  inLineEnd: boolean;
}

export interface VariantFocusTarget {
  attributeId: number;
  optionId: number;
}

export interface VariantSelectorSliceState {
  // Whether the "Show All" expanded view is active, keyed by variant attribute name.
  // Lives in the store so it survives navigating between variants, which remounts the picker.
  expandedByAttribute: Record<string, boolean>;
  /** New product page rows, keyed by variantRowKey, so a row looks the same after a variant loads. */
  rows: Record<string, VariantRowState>;
  /** Option picked from the keyboard, whose tile takes focus when its page mounts. */
  focusTarget: VariantFocusTarget | null;
  /** A variant was picked and its page hasn't finished loading its selector yet. */
  navigationPending: boolean;
}

const initialState: VariantSelectorSliceState = {
  expandedByAttribute: {},
  rows: {},
  focusTarget: null,
  navigationPending: false,
};

export const EMPTY_VARIANT_ROW: VariantRowState = {
  expanded: false,
  hasOverflow: false,
  scrollLeft: 0,
  hasLeftStart: false,
  inLineStart: false,
  inLineEnd: false,
};

export const variantSelectorSlice = createSlice({
  name: "variantSelectorSlice",
  initialState,
  reducers: {
    setVariantAttributeExpanded: (state, action: PayloadAction<{ attributeName: string; expanded: boolean }>) => {
      state.expandedByAttribute[action.payload.attributeName] = action.payload.expanded;
    },
    updateVariantRow: (state, action: PayloadAction<{ key: string; row: Partial<VariantRowState> }>) => {
      state.rows[action.payload.key] = {
        ...EMPTY_VARIANT_ROW,
        ...state.rows[action.payload.key],
        ...action.payload.row,
      };
    },
    setVariantFocusTarget: (state, action: PayloadAction<VariantFocusTarget | null>) => {
      state.focusTarget = action.payload;
    },
    setVariantNavigationPending: (state, action: PayloadAction<boolean>) => {
      state.navigationPending = action.payload;
    },
  },
});

export const { setVariantAttributeExpanded, updateVariantRow, setVariantFocusTarget, setVariantNavigationPending } =
  variantSelectorSlice.actions;
export default variantSelectorSlice;
